import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Target, Calendar, Clock, ChevronRight, Download, Share, Plus,
  Smartphone, Monitor, Home, HeartPulse, Sparkles, ShieldCheck,
  Users, GraduationCap, Dumbbell, Building2, ClipboardList, LineChart, KeyRound,
} from 'lucide-react';
import {
  subscribeInstallPrompt, triggerInstallPrompt, enableInstallCapture,
  isIOS as detectIOS, isStandalone as detectStandalone,
} from '../utils/installPromptStore';

const PRIMARY_CTA_LABEL = 'Build My Workout';

// ─── PWA Install Hook (shared store; opts into capture for landing page) ───
const useInstallPrompt = () => {
  const [event, setEvent] = useState(null);
  useEffect(() => {
    enableInstallCapture();
    return subscribeInstallPrompt(setEvent);
  }, []);
  return {
    isInstallable: !!event,
    isIOS: detectIOS(),
    isStandalone: detectStandalone(),
    promptInstall: triggerInstallPrompt,
  };
};

// ─── HERO SECTION ───
const HeroSection = ({ onStartWorkout, onInstall, onStartOrg }) => (
  <section className="relative min-h-[90vh] flex items-center justify-center px-4 sm:px-6 overflow-hidden" data-testid="hero-section">
    {/* Background glow */}
    <div className="absolute inset-0 bg-gradient-to-b from-blood-950/20 via-transparent to-transparent" />
    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blood-600/5 rounded-full blur-3xl" />

    <div className="relative z-10 max-w-3xl mx-auto text-center">
      {/* Logo */}
      <div className="mb-8 flex justify-center">
        <img src="/icon-192x192.png" alt="Anvil & Ash" className="w-20 h-20 rounded-2xl shadow-2xl" />
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-6" data-testid="hero-headline">
        Stop guessing<br />
        <span className="text-blood-500">your workouts.</span>
      </h1>

      <p className="text-lg sm:text-xl text-stone-300 max-w-2xl mx-auto mb-3 leading-relaxed">
        Custom workouts built around your goals, equipment, and schedule.
      </p>
      <p className="text-sm sm:text-base text-stone-500 max-w-xl mx-auto mb-10">
        For individuals — and for the coaches, gyms, and schools who train them.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <button
          onClick={onStartWorkout}
          className="group w-full sm:w-auto px-8 py-4 bg-blood-600 hover:bg-blood-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-blood-600/20 hover:shadow-blood-600/40 flex items-center justify-center gap-2"
          data-testid="hero-cta-start"
        >
          {PRIMARY_CTA_LABEL}
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>

        <button
          onClick={onStartOrg}
          className="group w-full sm:w-auto px-8 py-4 bg-amber-700 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-amber-700/20 hover:shadow-amber-600/40 flex items-center justify-center gap-2"
          data-testid="hero-cta-org"
        >
          <Users className="w-5 h-5" />
          For Coaches &amp; Teams
        </button>
      </div>

      <button
        onClick={onInstall}
        className="mt-4 mx-auto px-6 py-3 border border-stone-700 hover:border-stone-500 text-stone-400 hover:text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
        data-testid="hero-cta-install"
      >
        <Download className="w-4 h-4" />
        Install on Phone
      </button>

      <p className="mt-6 text-stone-600 text-sm">Personal training is free forever. No credit card needed.</p>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 border-2 border-stone-700 rounded-full flex justify-center pt-2">
        <div className="w-1.5 h-3 bg-stone-600 rounded-full" />
      </div>
    </div>
  </section>
);

// ─── VALUE PROPOSITION ───
const valueCards = [
  { icon: Zap, title: 'No guesswork', desc: 'Your workout is built instantly around your goals, equipment, and experience level.' },
  { icon: Target, title: 'Train anywhere', desc: 'Home or gym — choose your setup and get a plan that works with what you have.' },
  { icon: Calendar, title: 'Stay consistent', desc: 'Structured sessions keep you on track. Log workouts, track streaks, see progress.' },
  { icon: Clock, title: 'Built for real life', desc: 'Fast, focused sessions. No fluff. Open the app, start training, get results.' },
];

const ValueSection = () => (
  <section className="py-24 px-4 sm:px-6" data-testid="value-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4 tracking-tight">
        Why people choose <span className="text-blood-500">Anvil &amp; Ash</span>
      </h2>
      <p className="text-stone-400 text-center mb-16 max-w-xl mx-auto text-base sm:text-lg">
        Everything you need to train smarter, not harder.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {valueCards.map((card, i) => (
          <div
            key={card.title}
            className="group p-6 rounded-2xl border border-stone-800 bg-stone-900/50 hover:border-blood-800/50 hover:bg-stone-900/80 transition-all"
            data-testid={`value-card-${i}`}
          >
            <div className="w-12 h-12 rounded-xl bg-blood-950/50 border border-blood-800/30 flex items-center justify-center mb-4 group-hover:border-blood-600/50 transition-colors">
              <card.icon className="w-6 h-6 text-blood-500" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
            <p className="text-stone-400 text-sm leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── WHO IT'S FOR ───
const personaCards = [
  { icon: Home, title: 'Home gym lifters', desc: 'Limited equipment? No problem. Tell us what you have and we work around it.' },
  { icon: Calendar, title: 'Busy parents & pros', desc: 'Sessions designed for tight schedules. 20 minutes counts when the workout is right.' },
  { icon: Sparkles, title: 'Beginners', desc: 'Clear instructions, sensible progression, no jargon. Start training with confidence.' },
  { icon: HeartPulse, title: 'People returning to fitness', desc: 'Pick your level and any injuries — get a plan that meets you where you are.' },
];

const WhoItsForSection = () => (
  <section className="py-24 px-4 sm:px-6 bg-stone-950/50" data-testid="who-its-for-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4 tracking-tight">
        Made for real-world training
      </h2>
      <p className="text-stone-400 text-center mb-16 max-w-2xl mx-auto text-base sm:text-lg">
        If complicated fitness apps have burned you out, this one is for you.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {personaCards.map((card, i) => (
          <div
            key={card.title}
            className="p-6 rounded-2xl border border-stone-800 bg-stone-900/40 hover:border-blood-800/40 transition-all"
            data-testid={`persona-card-${i}`}
          >
            <div className="w-11 h-11 rounded-lg bg-stone-900 border border-stone-700 flex items-center justify-center mb-4">
              <card.icon className="w-5 h-5 text-blood-400" />
            </div>
            <h3 className="text-white font-semibold text-base mb-2">{card.title}</h3>
            <p className="text-stone-400 text-sm leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── FOR ORGANIZATIONS ───
const orgExamples = [
  { icon: GraduationCap, title: 'Schools & athletic programs', desc: 'Athletic directors invite entire rosters free. Coaches see who trained, who ate, and who\u2019s falling behind — before game day.' },
  { icon: Dumbbell, title: 'Gyms & fitness studios', desc: 'Give every member a training app with your coaching on top. Assign plans, watch compliance, keep members longer.' },
  { icon: Users, title: 'Personal trainers', desc: 'Your clients log workouts and meals; you see everything in one dashboard. Adjust their plans and macros between sessions.' },
  { icon: Building2, title: 'Sports teams & clubs', desc: 'Organize squads into teams, assign coaches to each, and track off-season training without chasing spreadsheets.' },
];

const orgFeatures = [
  { icon: LineChart, text: 'Live workout & nutrition compliance dashboards' },
  { icon: ClipboardList, text: 'Assign personalized or fully custom workout plans' },
  { icon: KeyRound, text: 'Invite athletes with a code or link — free for them, always' },
  { icon: ShieldCheck, text: 'Role-based access: admins, coaches, nutritionists' },
];

const orgTiers = [
  { name: 'Individual Coach', price: '$39/mo' },
  { name: 'Small Team / Gym', price: '$99/mo' },
  { name: 'School / Organization', price: '$199/mo' },
  { name: 'Enterprise', price: 'Custom' },
];

const ForOrganizationsSection = ({ onStartOrg, onJoinCode }) => (
  <section id="for-organizations" className="py-24 px-4 sm:px-6 bg-gradient-to-b from-stone-950/80 to-transparent border-y border-amber-900/20" data-testid="for-organizations-section">
    <div className="max-w-6xl mx-auto">
      <p className="text-center text-amber-500 text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-4">Anvil &amp; Ash for Teams</p>
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4 tracking-tight">
        Built for <span className="text-amber-400">coaches, gyms &amp; schools</span>
      </h2>
      <p className="text-stone-400 text-center mb-14 max-w-2xl mx-auto text-base sm:text-lg">
        Your organization pays one subscription. Your athletes train free — and you see every workout, meal, and weight trend in one dashboard.
      </p>

      {/* Example cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
        {orgExamples.map((card, i) => (
          <div
            key={card.title}
            className="p-6 rounded-2xl border border-stone-800 bg-stone-900/40 hover:border-amber-800/50 hover:bg-stone-900/70 transition-all"
            data-testid={`org-example-card-${i}`}
          >
            <div className="w-12 h-12 rounded-xl bg-amber-950/50 border border-amber-800/30 flex items-center justify-center mb-4">
              <card.icon className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-white font-bold text-base mb-2">{card.title}</h3>
            <p className="text-stone-400 text-sm leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>

      {/* Feature strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto mb-14">
        {orgFeatures.map((f) => (
          <div key={f.text} className="flex items-center gap-3 text-stone-300 text-sm bg-stone-900/40 border border-stone-800 rounded-xl px-4 py-3">
            <f.icon className="w-4 h-4 text-amber-400 shrink-0" />
            {f.text}
          </div>
        ))}
      </div>

      {/* Pricing strip */}
      <div className="flex flex-wrap justify-center gap-3 mb-4" data-testid="org-pricing-strip">
        {orgTiers.map((t) => (
          <div key={t.name} className="px-4 py-2 rounded-full border border-stone-700 bg-stone-900/60 text-sm">
            <span className="text-stone-400">{t.name}</span>
            <span className="text-amber-400 font-bold ml-2">{t.price}</span>
          </div>
        ))}
      </div>
      <p className="text-center text-amber-500/80 text-xs uppercase tracking-widest mb-10">Free during beta — billing coming soon</p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <button
          onClick={onStartOrg}
          className="group w-full sm:w-auto px-8 py-4 bg-amber-700 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-amber-700/20 hover:shadow-amber-600/40 flex items-center justify-center gap-2"
          data-testid="org-cta-start"
        >
          Start Your Organization
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <button
          onClick={onJoinCode}
          className="w-full sm:w-auto px-8 py-4 border border-stone-700 hover:border-amber-700 text-stone-300 hover:text-amber-300 font-semibold text-lg rounded-xl transition-all flex items-center justify-center gap-2"
          data-testid="org-cta-join"
        >
          <KeyRound className="w-5 h-5" />
          Athlete with an invite code?
        </button>
      </div>
    </div>
  </section>
);

// ─── HOW IT WORKS ───
const steps = [
  { num: '01', title: 'Tell us about you', desc: 'Pick your fitness level, equipment, schedule, and any injuries to work around.' },
  { num: '02', title: 'Generate your plan', desc: 'AI builds a personalized workout tailored to your body and goals — in seconds.' },
  { num: '03', title: 'Train and track', desc: 'Follow along, log your sets, watch your streak climb. Done.' },
];

const HowItWorks = () => (
  <section className="py-24 px-4 sm:px-6" data-testid="how-it-works-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-16 tracking-tight">
        How it works
      </h2>

      <div className="space-y-0">
        {steps.map((step, i) => (
          <div key={step.num} className="flex gap-6 items-start" data-testid={`step-${i}`}>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full border-2 border-blood-700 bg-blood-950/50 flex items-center justify-center flex-shrink-0">
                <span className="text-blood-400 font-bold text-sm">{step.num}</span>
              </div>
              {i < steps.length - 1 && <div className="w-px h-16 bg-stone-800" />}
            </div>

            <div className="pb-12">
              <h3 className="text-white font-bold text-xl mb-1.5">{step.title}</h3>
              <p className="text-stone-400 text-sm sm:text-base leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── SCREENSHOT GALLERY ───
const screenshots = [
  { src: '/play-store/screenshot-1-hub.png', caption: 'Your command center', alt: 'Main hub with all features' },
  { src: '/play-store/screenshot-2-workout.png', caption: 'AI-built workout plans', alt: 'AI generated workout plan' },
  { src: '/play-store/screenshot-3-progress.png', caption: 'Track every gain', alt: 'Progress and weight tracking' },
  { src: '/play-store/screenshot-4-armory.png', caption: 'Curated gear picks', alt: 'Supplement and gear shop' },
];

const ScreenshotGallery = () => (
  <section className="py-24 px-4 sm:px-6 bg-stone-950/50" data-testid="screenshot-section">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4 tracking-tight">
        See it in action
      </h2>
      <p className="text-stone-400 text-center mb-16 max-w-lg mx-auto text-base sm:text-lg">
        Everything you need in your pocket.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
        {screenshots.map((ss, i) => (
          <div key={i} className="group" data-testid={`screenshot-${i}`}>
            <div className="rounded-3xl overflow-hidden border border-stone-800 group-hover:border-stone-700 transition-all shadow-2xl bg-stone-900">
              <img
                src={ss.src}
                alt={ss.alt}
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
            <p className="text-stone-300 text-sm sm:text-base text-center mt-4 font-medium">{ss.caption}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── SOCIAL PROOF / TRUST ───
const trustCards = [
  {
    icon: Clock,
    title: 'Designed for busy schedules',
    desc: 'Sessions that fit a real life — 20, 45, or 60 minutes. You set the time, we build the plan.',
  },
  {
    icon: ShieldCheck,
    title: 'Simple, not bloated',
    desc: 'No 12-tab onboarding. No paywalls in the way. Workout generation in a couple of taps.',
  },
  {
    icon: Sparkles,
    title: 'Built by real lifters',
    desc: 'Made by people who train. No influencer fluff, no celebrity programs, no fake guarantees.',
  },
];

const SocialProofSection = () => (
  <section className="py-24 px-4 sm:px-6" data-testid="social-proof-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4 tracking-tight">
        BUILT FOR REAL TRAINING
      </h2>
      <p className="text-stone-400 text-center mb-16 max-w-2xl mx-auto text-base sm:text-lg">
        A simple, honest training tool. No hype, no hidden agenda.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {trustCards.map((card, i) => (
          <div
            key={card.title}
            className="p-7 rounded-2xl border border-stone-800 bg-stone-900/50 hover:border-blood-800/40 transition-all"
            data-testid={`trust-card-${i}`}
          >
            <div className="w-12 h-12 rounded-xl bg-blood-950/50 border border-blood-800/30 flex items-center justify-center mb-5">
              <card.icon className="w-6 h-6 text-blood-500" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
            <p className="text-stone-400 text-sm leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>

      {/* Honest stat row */}
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
        <div className="py-4 px-6 rounded-xl border border-stone-800 bg-stone-900/40">
          <p className="text-blood-500 text-2xl font-black tracking-tight">100%</p>
          <p className="text-stone-400 text-xs sm:text-sm uppercase tracking-wider mt-1">Free to start</p>
        </div>
        <div className="py-4 px-6 rounded-xl border border-stone-800 bg-stone-900/40">
          <p className="text-blood-500 text-2xl font-black tracking-tight">Daily</p>
          <p className="text-stone-400 text-xs sm:text-sm uppercase tracking-wider mt-1">New workouts generated</p>
        </div>
        <div className="py-4 px-6 rounded-xl border border-stone-800 bg-stone-900/40">
          <p className="text-blood-500 text-2xl font-black tracking-tight">Growing</p>
          <p className="text-stone-400 text-xs sm:text-sm uppercase tracking-wider mt-1">Beta community</p>
        </div>
      </div>
    </div>
  </section>
);

// ─── INSTALL SECTION ───
const InstallSection = ({ isInstallable, isIOS, isStandalone, promptInstall }) => {
  const [showInstructions, setShowInstructions] = useState(false);

  if (isStandalone) return null;

  return (
    <section className="py-24 px-4 sm:px-6 bg-stone-950/50" data-testid="install-section">
      <div className="max-w-3xl mx-auto text-center">
        <div className="w-16 h-16 rounded-2xl bg-blood-950/50 border border-blood-800/30 flex items-center justify-center mx-auto mb-6">
          <Smartphone className="w-8 h-8 text-blood-500" />
        </div>

        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
          Install it like a real app
        </h2>
        <p className="text-stone-400 mb-10 max-w-md mx-auto text-base sm:text-lg">
          Add Anvil &amp; Ash to your home screen for instant access, offline support, and full-screen mode.
        </p>

        {isInstallable ? (
          <button
            onClick={promptInstall}
            className="px-8 py-4 bg-blood-600 hover:bg-blood-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-blood-600/20 inline-flex items-center gap-2"
            data-testid="install-native-btn"
          >
            <Download className="w-5 h-5" />
            Install Now
          </button>
        ) : (
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="px-8 py-4 border border-stone-700 hover:border-stone-500 text-stone-300 hover:text-white font-semibold rounded-xl transition-all inline-flex items-center gap-2"
            data-testid="install-manual-btn"
          >
            <Download className="w-5 h-5" />
            {showInstructions ? 'Hide Instructions' : 'How to Install'}
          </button>
        )}

        {showInstructions && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
            {/* Android */}
            <div className="p-6 rounded-2xl border border-stone-800 bg-stone-900/50">
              <div className="flex items-center gap-3 mb-4">
                <Monitor className="w-5 h-5 text-green-500" />
                <h3 className="text-white font-bold">Android (Chrome)</h3>
              </div>
              <ol className="space-y-3 text-stone-400 text-sm">
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">1.</span>
                  Tap the <strong className="text-stone-300">three-dot menu</strong> in Chrome
                </li>
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">2.</span>
                  Tap <strong className="text-stone-300">"Install app"</strong> or <strong className="text-stone-300">"Add to Home screen"</strong>
                </li>
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">3.</span>
                  Tap <strong className="text-stone-300">"Install"</strong> to confirm
                </li>
              </ol>
            </div>

            {/* iOS */}
            <div className="p-6 rounded-2xl border border-stone-800 bg-stone-900/50">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="w-5 h-5 text-blue-500" />
                <h3 className="text-white font-bold">iPhone (Safari)</h3>
              </div>
              <ol className="space-y-3 text-stone-400 text-sm">
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">1.</span>
                  Tap the <Share className="w-4 h-4 inline text-blue-400" /> <strong className="text-stone-300">Share</strong> button at the bottom
                </li>
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">2.</span>
                  Scroll and tap <Plus className="w-4 h-4 inline text-stone-300" /> <strong className="text-stone-300">"Add to Home Screen"</strong>
                </li>
                <li className="flex gap-3">
                  <span className="text-blood-500 font-bold">3.</span>
                  Tap <strong className="text-stone-300">"Add"</strong> — it will appear like a native app
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ─── FINAL CTA SECTION ───
const CTASection = ({ onStartWorkout, onInstall, onStartOrg }) => (
  <section className="py-28 px-4 sm:px-6" data-testid="cta-section">
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-5 leading-[1.1] tracking-tight">
        Start your first workout{' '}<br />
        <span className="text-blood-500">in seconds.</span>
      </h2>
      <p className="text-stone-400 mb-10 text-base sm:text-lg">No signup wall. No credit card. Just results.</p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <button
          onClick={onStartWorkout}
          className="group w-full sm:w-auto px-8 py-4 bg-blood-600 hover:bg-blood-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-blood-600/20 hover:shadow-blood-600/40 flex items-center justify-center gap-2"
          data-testid="cta-start"
        >
          {PRIMARY_CTA_LABEL}
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>

        <button
          onClick={onInstall}
          className="w-full sm:w-auto px-8 py-4 border border-stone-700 hover:border-stone-500 text-stone-300 hover:text-white font-semibold text-lg rounded-xl transition-all flex items-center justify-center gap-2"
          data-testid="cta-install"
        >
          <Download className="w-5 h-5" />
          Install on Phone
        </button>
      </div>

      <button
        onClick={onStartOrg}
        className="mt-8 text-amber-400 hover:text-amber-300 text-sm font-semibold transition-colors inline-flex items-center gap-1.5"
        data-testid="cta-org-link"
      >
        <Users className="w-4 h-4" />
        Coach, gym, or school? Set up your organization →
      </button>
    </div>
  </section>
);

// ─── FOOTER ───
const Footer = () => (
  <footer className="py-10 px-4 sm:px-6 border-t border-stone-900">
    <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src="/icon-72x72.png" alt="Anvil & Ash" className="w-8 h-8 rounded-lg" />
        <span className="text-stone-500 text-sm">Anvil &amp; Ash Fitness</span>
      </div>
      <div className="flex gap-6">
        <a href="/privacy-policy" className="text-stone-500 hover:text-stone-300 text-sm transition-colors">Privacy Policy</a>
        <a href="/terms-of-service" className="text-stone-500 hover:text-stone-300 text-sm transition-colors">Terms of Service</a>
      </div>
    </div>
  </footer>
);

// ─── MAIN LANDING PAGE ───
const MarketingLanding = () => {
  const navigate = useNavigate();
  const install = useInstallPrompt();

  useEffect(() => {
    if (!sessionStorage.getItem('aa_visit_tracked')) {
      sessionStorage.setItem('aa_visit_tracked', '1');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: window.location.pathname, referrer: document.referrer }),
      }).catch(() => {});
    }
  }, []);

  const handleStartWorkout = () => {
    navigate('/auth');
  };

  const handleStartOrg = () => {
    if (localStorage.getItem('token')) {
      navigate('/teams');
      return;
    }
    localStorage.setItem('post_auth_redirect', '/teams');
    navigate('/auth');
  };

  const handleOrgScroll = () => {
    document.getElementById('for-organizations')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInstall = () => {
    if (install.isInstallable) {
      install.promptInstall();
    } else {
      document.querySelector('[data-testid="install-section"]')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-950">
      <HeroSection onStartWorkout={handleStartWorkout} onInstall={handleInstall} onStartOrg={handleOrgScroll} />
      <ValueSection />
      <WhoItsForSection />
      <ForOrganizationsSection onStartOrg={handleStartOrg} onJoinCode={handleStartOrg} />
      <HowItWorks />
      <ScreenshotGallery />
      <SocialProofSection />
      <InstallSection {...install} />
      <CTASection onStartWorkout={handleStartWorkout} onInstall={handleInstall} onStartOrg={handleStartOrg} />
      <Footer />
    </div>
  );
};

export default MarketingLanding;
